import { Directive, inject, Input, OnInit, Signal } from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { find } from 'lodash';
import { BehaviorSubject, combineLatest, debounceTime, filter, Subscription } from 'rxjs';
import { TReducerState } from '../../../app.config';
import { IDataRefreshRequestParams } from '../../../shared/components/datatable/datatable.interface';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { PageConfigurationComponent } from '../../../shared/components/page-configuration/page-configuration.component';
import {
  EWidgetSortBy,
  IApplyData,
  IConfigurationData,
} from '../../../shared/components/page-configuration/page-configuration.interface';
import { userActions } from '../../../state/user/user.actions';
import {
  IPageConfigurationValue,
  IUser,
  TBoardSortConfiguration,
  TItemsComponentDatatableSortConfiguration,
  TItemsComponentPageConfiguration,
} from '../../../state/user/user.interface';
import { DateUtilitiesService } from '../../../utilities/date-utilities.service';
import { IGenericCrudRequestConstructionParameters } from '../../../utilities/http-utilities.service';
import { ESnackbar } from '../../../utilities/snackbar.service';
import { BaseCrudPageComponent } from '../../app/base-crud-page.component';
import { IItemsFilterCardFields, TItemView } from '../items-filter-card/items-filter-card.interface';
import { ItemsModalComponent } from '../items-modal/items-modal.component';
import { IBoardTreeStructure, IItemJoins } from '../items.interface';
import { ItemsService } from '../items.service';
import { IItemDatatableState, ItemViewStore } from './item-view.store';

import { defaultGeneralInformationDatatableColumns } from './items-datatable/items-datatable.interface';
import { ERowClass, IDatatableItem } from './items-kanban/items-kanban.interface';

@Directive({ providers: [ItemViewStore] })
export class BaseItemView
  extends BaseCrudPageComponent<IItemJoins<string>, IItemDatatableState<string>, IItemsFilterCardFields>
  implements OnInit
{
  @Input({ required: true }) public override filterCardData$: BehaviorSubject<Partial<IItemsFilterCardFields> | null> =
    new BehaviorSubject<Partial<IItemsFilterCardFields> | null>(null);
  @Input() public boardTreeStructure: IBoardTreeStructure[] = [];

  public readonly globalStore: Store<any> = inject(Store<TReducerState>);
  protected store: ItemViewStore;
  protected itemsService: ItemsService = inject(ItemsService);
  protected actionSubject: ActionsSubject = inject(ActionsSubject);

  protected selectedBoard: IDropdownOption | null = null;
  protected isSelfRefreshTrigger = false;
  protected appliedSortBy: EWidgetSortBy | undefined;

  constructor(private readonly viewName: TItemView) {
    const store: ItemViewStore = inject(ItemViewStore);
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfItems',
      multiContext: 'field.actions',
      nameProperty: 'name',
      singleContext: 'field.action',
    });

    this.store = store;
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.getDataRefreshSubscription(),
      this.itemsService.refreshItems$.subscribe(() => {
        if (this.isSelfRefreshTrigger) {
          this.isSelfRefreshTrigger = false;

          return;
        }

        this.getData(this.filterCardData$.value as IItemsFilterCardFields, this.datatableParams$.value!);
      }),
      this.setPageToOneAndRefreshDatatable$.subscribe(() => {
        this.isSelfRefreshTrigger = true;
        this.itemsService.triggerRefresh();
      }),
      PageConfigurationComponent.onApply
        .pipe(filter(Boolean))
        .subscribe(({ configurations }: IApplyData<string | number>) => {
          const datatableConfig: IConfigurationData<string | number> | undefined = configurations.find(
            ({ type }) => type === 'datatable',
          );
          const appliedSortBy = datatableConfig?.sortBy as EWidgetSortBy | undefined;
          const currentActiveSortBy: EWidgetSortBy | undefined =
            this.appliedSortBy ?? (this.getSavedSortRawValue() as EWidgetSortBy | undefined);

          if (appliedSortBy && appliedSortBy === currentActiveSortBy) {
            this.appliedSortBy = appliedSortBy;

            return;
          }

          this.appliedSortBy = appliedSortBy;

          const { value: currentDatatableParams } = this.datatableParams$;
          const { value: currentFilterCardData } = this.filterCardData$;

          if (!currentDatatableParams) {
            return;
          }

          if (appliedSortBy) {
            const parsedSort: Sort = this.parseSortStringToSortObject(appliedSortBy);

            this.datatableParams$.next({
              ...currentDatatableParams,
              sort: [this.prepareSort(parsedSort)] as unknown as Sort,
            });
          } else {
            this.resetDatatableParamsSort();

            if (currentFilterCardData) {
              this.getData(currentFilterCardData as IItemsFilterCardFields, currentDatatableParams);
            }
          }
        }),
      this.actionSubject
        .pipe(ofType(userActions.updateUserConfigurationsCompleted.type))
        .subscribe(({ omitSuccessToast }: ReturnType<typeof userActions.updateUserConfigurationsCompleted>) => {
          if (!omitSuccessToast) {
            this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
          }

          this.appliedSortBy = undefined;
        }),
    );
  }

  private resetDatatableParamsSort(): void {
    const currentParams: IDataRefreshRequestParams | null = this.datatableParams$.value;

    if (currentParams) {
      const { sort, ...paramsWithoutSort } = currentParams as Partial<IDataRefreshRequestParams>;

      this.datatableParams$.next(paramsWithoutSort as IDataRefreshRequestParams);
    }
  }

  protected loadItems(filterCard: IItemsFilterCardFields, datatable: IDataRefreshRequestParams): void {
    const params: IGenericCrudRequestConstructionParameters = this.createRequestParams(filterCard, datatable);

    this.store.loadItems(params);
  }

  protected createRequestParams(
    filterCard: IItemsFilterCardFields,
    datatable: IDataRefreshRequestParams,
  ): IGenericCrudRequestConstructionParameters {
    return {
      fields: [
        'name',
        'description',
        'key',
        'boardId',
        'boardItemConfigurationId',
        'fieldSetId',
        'currentWorkflowStepId',
        'assigneeId',
        'createdBy',
        'dueDate',
        'createdAt',
        'values',
        'rank',
        'escalatedBoardId',
        'itemRecurrenceId',
        'eventDate',
        'shiftId',
        'strikeCount',
        'priority',
      ],
      filters: [...this.itemsService.prepareFilters(filterCard)],
      join: [
        'board||name,key',
        'boardItemConfiguration||name,key,itemType',
        'boardItemConfiguration.itemCategory||name,isDefault,isActive',
        'itemGuideAssignments||guideId',
        'itemGuideAssignments.guide||name',
        'currentWorkflowStep||name,isDefault,statusCategory',
        'assignee||name',
        'reporter||name',
      ],
      limit: datatable.limit,
      page: datatable.page,
      sort: this.setSort(datatable),
      ...(datatable.search
        ? { search: { searchedFields: ['name', 'key', 'board.name'], searchText: datatable.search ?? '' } }
        : {}),
      additionalCustomSearch: this.itemsService.prepareAdditionalCustomSearch(filterCard, this.boardTreeStructure),
    };
  }

  private setSort(datatable: IDataRefreshRequestParams): Sort[] {
    if (datatable.sort) {
      if (Array.isArray(datatable.sort) && datatable.sort.length > 0) {
        return datatable.sort.map((sortItem: Sort) => this.prepareSort(sortItem));
      } else if (!Array.isArray(datatable.sort) && datatable.sort.active && datatable.sort.active !== 'id') {
        return [this.prepareSort(datatable.sort)];
      }
    }

    const userConfigSort: Sort | null = this.getSavedSortFromUserConfiguration();

    if (userConfigSort) {
      return [this.prepareSort(userConfigSort)];
    }

    return this.getDefaultSort();
  }

  protected getSavedSortRawValue(): string | null {
    const userSignal: Signal<IUser | null> = this.globalStore.selectSignal(
      (state: TReducerState): IUser | null => state.user.user,
    );

    const sortConfiguration: TItemsComponentDatatableSortConfiguration | undefined =
      this.extractSortConfiguration(userSignal());

    if (!sortConfiguration?.value || !Array.isArray(sortConfiguration.value)) {
      return null;
    }

    const selectedBoardIds: string[] = this.getSelectedBoardIds();
    const matchedBoardSort: IPageConfigurationValue<string, string>[] | null | undefined = this.findMatchedBoardSort(
      sortConfiguration.value,
      selectedBoardIds,
    );

    if (!Array.isArray(matchedBoardSort) || !matchedBoardSort.length) {
      return null;
    }

    const [firstSortItem]: (IPageConfigurationValue<string, string> | string)[] = matchedBoardSort;

    return this.formatSortItemToString(firstSortItem);
  }

  private extractSortConfiguration(user: IUser | null): TItemsComponentDatatableSortConfiguration | undefined {
    const itemsComponentConfig: TItemsComponentPageConfiguration[] | undefined =
      user?.pageConfiguration?.ItemsComponent;

    if (!itemsComponentConfig) {
      return undefined;
    }

    return itemsComponentConfig.find(
      (comp: TItemsComponentPageConfiguration): comp is TItemsComponentDatatableSortConfiguration =>
        comp.name === 'datatableSort',
    );
  }

  private getSelectedBoardIds(): string[] {
    const boardFilter: IDropdownOption<number, string>[] | null | undefined = this.filterCardData$.value?.board;

    return boardFilter?.length ? boardFilter.map(({ id }: IDropdownOption<number, string>): string => String(id)) : [];
  }

  private findMatchedBoardSort(
    sortValues: TBoardSortConfiguration[],
    selectedBoardIds: string[],
  ): IPageConfigurationValue<string, string>[] | null | undefined {
    if (!selectedBoardIds.length) {
      return sortValues.find(({ name }: TBoardSortConfiguration): boolean => name === 'items')?.value;
    }

    const matchedConfig: TBoardSortConfiguration | undefined = sortValues.find(
      ({ name }: TBoardSortConfiguration): boolean => selectedBoardIds.includes(String(name)),
    );

    return matchedConfig?.value;
  }

  private formatSortItemToString(
    sortItem: IPageConfigurationValue<string, string> | string | undefined,
  ): string | null {
    if (!sortItem) {
      return null;
    }

    if (typeof sortItem === 'string') {
      return sortItem;
    }

    const { name, value }: IPageConfigurationValue<string, string> = sortItem;

    if (name && value) {
      return `${name}_${value.toLowerCase()}`;
    }

    return null;
  }

  protected getSavedSortFromUserConfiguration(): Sort | null {
    const rawSortValue: string | null = this.getSavedSortRawValue();

    if (!rawSortValue) {
      return null;
    }

    return this.parseSortStringToSortObject(rawSortValue);
  }

  private getColumnNameById(columnIdOrName: string): string {
    return (
      defaultGeneralInformationDatatableColumns.find((col) => col.id === Number(columnIdOrName))?.name ?? columnIdOrName
    );
  }

  private parseSortStringToSortObject(sortValue: string): Sort {
    const lastUnderscoreIndex: number = sortValue.lastIndexOf('_');

    if (lastUnderscoreIndex === -1) {
      return { active: this.getColumnNameById(sortValue), direction: 'asc' };
    }

    const rawName: string = sortValue.slice(0, lastUnderscoreIndex);
    const rawDirection: string = sortValue.slice(lastUnderscoreIndex + 1).toLowerCase();

    return {
      active: this.getColumnNameById(rawName),
      direction: rawDirection === 'desc' ? 'desc' : 'asc',
    };
  }

  private getDefaultSort(): Sort[] {
    return [
      { active: 'dueDate', direction: 'asc' as SortDirection },
      { active: 'createdAt', direction: 'desc' as SortDirection },
    ];
  }

  protected initializeAdd(): void {
    this.setupForAdd();

    this.matDialog.open(ItemsModalComponent, {
      ...ItemsModalComponent.modalParameters,
      data: {
        submitButtonText: this.addEditSubmitButtonText(),
        title: this.addEditModalTitle(),
        ...(this.selectedBoard ? { selectedBoard: this.selectedBoard } : {}),
      },
      restoreFocus: false,
    });
  }

  protected initializeEdit(clickedItemId?: number): void {
    this.setupForEdit();
    const [item] = this.selectedRows();
    const currentItem = clickedItemId ? find(this.store.data$(), { id: clickedItemId })! : item;
    this.itemsService.openItemEditDialogAndSetUrl(
      currentItem.board.key,
      currentItem.id,
      currentItem.key,
      this.addEditSubmitButtonText(),
    );
  }

  protected formatData = (item: IItemJoins<string>): IDatatableItem => {
    const descriptionCharacterLimit = 30;
    const description = this.removeHtmlTags(item.description ?? '')?.slice(0, descriptionCharacterLimit) ?? '';

    return {
      assignedWorkInstructions: item.itemGuideAssignments,
      assignee: item.assignee?.name ?? '',
      boardId: item.board.id,
      boardItemConfiguration: item.boardItemConfiguration,
      boardKey: item.board.key,
      boardName: item.board.name,
      createdAt: DateUtilitiesService.convertUTCToUserFormatted(item.createdAt, true),
      currentWorkflowStepId: item.currentWorkflowStepId,
      description: description?.length === descriptionCharacterLimit ? `${description}...` : description,
      dueDate: item.dueDate
        ? { formatted: DateUtilitiesService.convertUTCToUserFormatted(item.dueDate, true), original: item.dueDate }
        : { formatted: '', original: '' },
      eventDate: DateUtilitiesService.convertUTCToUserFormatted(item.eventDate, true),
      id: item.id,
      itemCategory: item.boardItemConfiguration.itemCategory?.isDefault
        ? this.translate.instant(`system.label.${item.boardItemConfiguration.itemCategory.name}`)
        : (item.boardItemConfiguration.itemCategory?.name ?? ''),
      itemKey: item.key,
      itemType: this.translate.instant(`business.${item.boardItemConfiguration.itemType}`),
      priority: item.priority,
      reporter: item.reporter?.name ?? '',
      rowClass: item.escalatedBoardId ? ERowClass.danger : null,
      shiftId: item.shiftId,
      shiftName: item.shift?.name ?? '',
      status: item.currentWorkflowStep?.isDefault
        ? this.translate.instant(`system.label.${item.currentWorkflowStep.name}`)
        : (item.currentWorkflowStep?.name ?? ''),
      statusCategory: item.currentWorkflowStep?.statusCategory,
      strikeCount: item.strikeCount,
      title: item.name,
      titleIcon: item.escalatedBoardId ? { class: 'escalated-board-icon', icon: 'arrow_upward' } : null,
      values: item.values,
    };
  };

  protected removeHtmlTags(html: string): string | null {
    const preProcessedHtml = html.replace(/<br\s*\/?>/gi, ' ').replace(/<\/(p|div|h[1-6]|li|ul|ol|blockquote)>/gi, ' ');

    const doc: Document = new DOMParser().parseFromString(preProcessedHtml, 'text/html');

    return doc.body.textContent;
  }

  private prepareSort(sort: Sort): Sort {
    const { active, direction } = sort;
    const columnName: string = this.getColumnNameById(active);

    switch (columnName) {
      case 'boardName':
        return { active: 'board.name', direction };
      case 'itemKey':
        return { active: 'key', direction };
      case 'assignee':
        return { active: 'assignee.name', direction };
      case 'reporter':
        return { active: 'reporter.name', direction };
      case 'title':
        return { active: 'name', direction };
      case 'status':
        return { active: 'currentWorkflowStep.name', direction };
      case 'itemType':
      case 'itemCategory':
        return { active: 'boardItemConfiguration.name', direction };
      default:
        return { active: columnName, direction };
    }
  }

  private getData(filterCard: IItemsFilterCardFields, datatable: IDataRefreshRequestParams): void {
    if (this.viewName === 'kanban') {
      // Kanban acts as if "this month" were selected whenever no `dateRange` chip is active — the
      // same rule kamishibai applies, and for the same reason: the fallback must never be written
      // back to the filter card, only used for this one query. List view is deliberately excluded
      // (falls through to the `else` branch below unmodified) — it requests with no date constraint
      // at all when the chip is absent. See `ItemsService.getEffectiveDateRange`.
      const filterCardForQuery: IItemsFilterCardFields = {
        ...filterCard,
        dateRange: this.itemsService.getEffectiveDateRange(filterCard.dateRange),
      };
      const apiParams = this.createRequestParams(filterCardForQuery, datatable);

      // The kanban drop logic needs the item's workflow transitions to compute valid target steps.
      apiParams.join = [
        ...(apiParams.join ?? []),
        'boardItemConfiguration.workflow||id',
        'boardItemConfiguration.workflow.workflowStepAssignments||id',
        'boardItemConfiguration.workflow.workflowStepAssignments.workflowStep||name,isDefault,statusCategory',
        'boardItemConfiguration.workflow.workflowStepAssignments.nextWorkflowStep||name,isDefault,statusCategory',
      ];

      this.store.loadItems(apiParams);
    } else {
      this.loadItems(filterCard as IItemsFilterCardFields, datatable);
    }
  }

  private getDataRefreshSubscription(): Subscription {
    return (
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
      })
        // `datatable`/`filterCard` can each emit their own follow-up update within the same tick
        // (e.g. a filter change also resetting the paginator to page 1) — debouncing collapses that
        // burst into the single, final combination instead of requesting once per intermediate one.
        .pipe(debounceTime(100))
        .subscribe(({ datatable, filterCard }) => {
          if (filterCard.itemView !== this.viewName) {
            return;
          }

          this.selectedBoard = filterCard.board?.length === 1 ? filterCard.board[0] : null;

          this.getData(filterCard as IItemsFilterCardFields, datatable);
        })
    );
  }
}
